import { FC, useEffect, useState } from 'react';
import { formatLocalTime, nightlyMaintenance } from './utils';

/**
 * Gold banner shown to EVERY user (not just Executives, unlike StaleBanner)
 * while the recsys nightly maintenance window is open — see utils.ts
 * `nightlyMaintenance` for the schedule and why searches fail inside it.
 * Re-evaluates every minute so a page left open rolls the banner in and out
 * without a reload. Pure display: nothing is disabled, the recruiter can still
 * try a search (it may well succeed early in the window).
 */

const GOLD_100 = '#FEF0D9';
const GOLD_200 = '#FBDBA0';
const GOLD_700 = '#B25E09';

const BANNER_STYLE: React.CSSProperties = {
  background: GOLD_100,
  border: `1px solid ${GOLD_200}`,
  color: GOLD_700,
  fontSize: 13,
  padding: '10px 16px',
  borderRadius: 6,
  marginTop: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const TICK_MS = 60_000;

interface MaintenanceBannerProps {
  /** Test seam: the clock to evaluate the window against. */
  now?: () => Date;
}

const MaintenanceBanner: FC<MaintenanceBannerProps> = ({ now = () => new Date() }) => {
  const [window, setWindow] = useState(() => nightlyMaintenance(now()));

  useEffect(() => {
    const timer = setInterval(() => setWindow(nightlyMaintenance(now())), TICK_MS);
    return () => clearInterval(timer);
  }, [now]);

  if (!window.active) return null;

  return (
    <div style={BANNER_STYLE} role="status" data-testid="maintenance-banner">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
        <path d="M8 1.5 1 14h14L8 1.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        <path d="M8 6.25v3.25" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="8" cy="11.75" r="0.9" fill="currentColor" />
      </svg>
      <span>
        <strong>Nightly index maintenance</strong> ({formatLocalTime(window.start)} – {formatLocalTime(window.end)}):
        Candidate Search may be unavailable or return no results until it finishes. Try again after{' '}
        {formatLocalTime(window.end)}.
      </span>
    </div>
  );
};

export default MaintenanceBanner;
